import { Role } from 'enums';
import { RoutePath } from 'enums/routePath';
import { useAppSelector } from 'hooks/reduxHooks';
import { get } from 'lodash-es';
import { UserViewModel } from 'models/User';
import * as modalLayoutPaths from 'pages/ModalLayoutPage/paths';
import routePaths from 'pages/routePaths';
import { useLocation } from 'react-router-dom';
import { getOne as getUser } from 'selectors/users';
import { hasAnyRole, hasRole } from 'utils/rbac';

const roleMapping = {
  [routePaths[RoutePath.SETTING_INVOICES]]: [Role.BILLING],
  [routePaths[RoutePath.SETTING_BILLING]]: [Role.BILLING],
  [modalLayoutPaths.SETTING_BILLING_SUBSCRIBE]: [Role.BILLING],
  [routePaths[RoutePath.SETTING_ACCOUNT_VISIBILITY]]: [Role.ADMIN],
};

interface IUseRolePermissions {
  hasRolePermissions: boolean;
}

export const useUserId = () =>
  useAppSelector((state) => state.auth?.user?.id || null);

export const useUser = (): UserViewModel | null =>
  useAppSelector((state) => getUser(state, get(state, 'auth.user.id')) || null);

export const useHasUserRole = (role: Role): boolean => {
  const user = useUser();

  if (!user) {
    return false;
  }

  return hasRole(role, user);
};

export const useHasAnyRole = (roles: Role[]): boolean => {
  const user = useUser();

  if (!user) {
    return false;
  }

  return hasAnyRole(roles, user);
};

export const useHasAdminRole = () => useHasUserRole(Role.ADMIN);
export const useHasBillingRole = () => useHasUserRole(Role.BILLING);
export const useHasManagerRole = () => useHasUserRole(Role.MANAGER);

export const useHasExportCSVPermission = () => {
  const accountDisable = useAppSelector(
    (state) =>
      !!get(state, 'account.account.settings.disableDataExport', false),
  );
  const hasAdminRole = useHasAdminRole();
  return hasAdminRole || !accountDisable;
};

const useRolePermissions = (): IUseRolePermissions => {
  const { pathname } = useLocation();

  const user = useAppSelector(
    (state) => getUser(state, get(state, 'auth.user.id')) || null,
  );
  const roles = roleMapping[pathname];

  if (!user && !roles) {
    return { hasRolePermissions: true };
  }

  if (!user && roles) {
    return { hasRolePermissions: false };
  }

  if (user) {
    const hasRolePermissions = !(roles && hasAnyRole(roles, user));
    return { hasRolePermissions };
  }
  return { hasRolePermissions: false };
};

export default useRolePermissions;
